import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Activity, CheckCircle, Eye, Clock, AlertCircle, 
  RefreshCw, Filter, MessageSquare, TrendingUp,
  Send, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RCSEventsLive({ embedded = false }) {
  const { token, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [filter, setFilter] = useState('all'); // all, delivered, read, failed

  // Fetch messages with status updates
  const fetchEvents = useCallback(async () => {
    try {
      const clientParam = selectedClient && selectedClient !== 'all' ? `&client_id=${selectedClient}` : '';
      const response = await fetch(
        `${API_URL}/api/v2/rcs/messages?limit=50${clientParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data.messages || []);
        calculateStats(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedClient]);

  const fetchClients = async () => {
    if (user?.role !== 'superadmin') return;
    
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const calculateStats = (messageList) => {
    const total = messageList.length;
    const sent = messageList.filter(m => m.status === 'sent').length;
    const delivered = messageList.filter(m => m.status === 'delivered').length;
    const read = messageList.filter(m => m.status === 'read').length;
    const failed = messageList.filter(m => m.status === 'failed').length;
    
    setStats({
      total,
      sent,
      delivered,
      read,
      failed,
      deliveryRate: total > 0 ? Math.round((delivered + read) / total * 100) : 0,
      readRate: delivered > 0 ? Math.round(read / (delivered + read) * 100) : 0
    });
  };

  useEffect(() => {
    fetchEvents();
    fetchClients();
  }, [fetchEvents]);

  // Auto refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchEvents, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchEvents]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'read':
        return <Eye className="w-4 h-4 text-purple-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { color: 'bg-blue-100 text-blue-700', label: 'Envoyé' },
      delivered: { color: 'bg-green-100 text-green-700', label: 'Délivré' },
      read: { color: 'bg-purple-100 text-purple-700', label: 'Lu' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Échoué' },
      pending: { color: 'bg-gray-100 text-gray-700', label: 'En attente' }
    };
    
    const { color, label } = config[status] || config.pending;
    return <Badge className={color}>{label}</Badge>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    // Mask middle digits for privacy
    if (phone.length > 8) {
      return phone.slice(0, 4) + '****' + phone.slice(-4);
    }
    return phone;
  };

  const filteredEvents = events.filter(e => {
    if (filter === 'all') return true;
    return e.status === filter;
  });

  const content = (
    <div className="space-y-6" data-testid="rcs-events-live">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Événements RCS en Direct
          </h1>
          <p className="text-gray-600 mt-1">
            Suivi temps réel des messages envoyés
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button variant="outline" onClick={fetchEvents}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-xl font-bold">{stats.total}</p>
                  </div>
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Envoyés</p>
                    <p className="text-xl font-bold text-blue-600">{stats.sent}</p>
                  </div>
                  <Send className="w-5 h-5 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Délivrés</p>
                    <p className="text-xl font-bold text-green-600">{stats.delivered}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Lus</p>
                    <p className="text-xl font-bold text-purple-600">{stats.read}</p>
                  </div>
                  <Eye className="w-5 h-5 text-purple-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Taux Livraison</p>
                    <p className="text-xl font-bold text-green-600">{stats.deliveryRate}%</p>
                  </div>
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Taux Lecture</p>
                    <p className="text-xl font-bold text-purple-600">{stats.readRate}%</p>
                  </div>
                  <Eye className="w-5 h-5 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">Filtres:</span>
              </div>
              
              {user?.role === 'superadmin' && (
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Tous les clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les clients</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <div className="flex gap-2">
                {['all', 'sent', 'delivered', 'read', 'failed'].map(f => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? 'default' : 'outline'}
                    onClick={() => setFilter(f)}
                    className={filter === f ? 'bg-indigo-600' : ''}
                  >
                    {f === 'all' && 'Tous'}
                    {f === 'sent' && 'Envoyés'}
                    {f === 'delivered' && 'Délivrés'}
                    {f === 'read' && 'Lus'}
                    {f === 'failed' && 'Échoués'}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Timeline des Messages</CardTitle>
            <CardDescription>
              {filteredEvents.length} message(s) affiché(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : filteredEvents.length > 0 ? (
              <div className="space-y-2">
                {filteredEvents.map((event, idx) => (
                  <div 
                    key={event.id || idx}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      {getStatusIcon(event.status)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{formatPhone(event.recipient_phone)}</span>
                        {getStatusBadge(event.status)}
                        {event.mock && (
                          <Badge variant="outline" className="text-xs">MOCK</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Type: {event.message_type} | 
                        Canal: {event.channel || 'RCS'}
                      </p>
                    </div>
                    
                    <div className="text-right text-xs text-gray-500 flex-shrink-0">
                      <div className="space-y-1">
                        {event.sent_at && (
                          <div className="flex items-center gap-1 justify-end">
                            <Send className="w-3 h-3" />
                            {formatTime(event.sent_at)}
                          </div>
                        )}
                        {event.delivered_at && (
                          <div className="flex items-center gap-1 justify-end text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            {formatTime(event.delivered_at)}
                          </div>
                        )}
                        {event.read_at && (
                          <div className="flex items-center gap-1 justify-end text-purple-600">
                            <Eye className="w-3 h-3" />
                            {formatTime(event.read_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucun message trouvé</p>
                <p className="text-sm text-gray-400 mt-1">
                  Les messages envoyés apparaîtront ici
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-gray-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Envoyé</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm">Délivré</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-purple-500" />
                <span className="text-sm">Lu</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm">Échoué</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
}
